import { resourceArtUrl } from '@/lib/artAssets';
import { haptic } from '@/lib/device';
import { duration, fmt, short, span } from '@/lib/format';
// Компонентная библиотека (дизайн-система): mobile-first, touch-таргеты ≥ 44px, шрифт ≥ 16px,
// фокус-кольца, aria-атрибуты, Esc и Tab-trap для модальных окон, анимации 150–250 мс.
import { serverNow } from '@/lib/serverTime';
import { play } from '@/lib/sound';
import { useGame } from '@/store/game';
import type { ResourceMap } from '@sk/shared';
import { type UiIcon, toDataUri, uiIcon } from '@sk/shared/icons';
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export function cx(...c: (string | false | null | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

// ───────── Графика из процедурного SVG ─────────
const uriCache = new Map<string, string>();
export function Svg({
  svg,
  alt,
  size = 40,
  className,
}: { svg: string; alt: string; size?: number; className?: string }) {
  const src = useMemo(() => {
    const cached = uriCache.get(svg);
    if (cached) return cached;
    const u = toDataUri(svg);
    uriCache.set(svg, u);
    return u;
  }, [svg]);
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      className={cx('inline-block select-none', className)}
      draggable={false}
    />
  );
}

export function Icon({
  name,
  label,
  size = 24,
  className,
}: { name: UiIcon; label?: string; size?: number; className?: string }) {
  return (
    <span
      className={cx('inline-flex shrink-0', className)}
      style={{ width: size, height: size }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      // SVG генерируется нашим кодом (доверенный источник), currentColor берёт цвет темы
      dangerouslySetInnerHTML={{ __html: uiIcon(name, label ?? name, size) }}
    />
  );
}

export function ResIcon({ type, size = 20 }: { type: keyof ResourceMap | 'gems'; size?: number }) {
  const { t } = useTranslation();
  return (
    <img
      src={resourceArtUrl(type)}
      alt={t(`resources.${type}`)}
      title={t(`resources.${type}`)}
      width={size}
      height={size}
      className="inline-block shrink-0 select-none object-contain"
      draggable={false}
    />
  );
}

// ───────── Кнопки и поля ─────────
type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'gem';
const variants: Record<Variant, string> = {
  primary: 'bg-gradient-to-b from-gold-400 to-gold-600 text-night border-gold-300 shadow-[0_2px_0_#5a4214]',
  secondary: 'bg-ink-700 text-parchment-100 border-ink-600',
  danger: 'bg-gradient-to-b from-blood-400 to-blood-500 text-white border-blood-400',
  ghost: 'bg-transparent text-parchment-200 border-transparent',
  gem: 'bg-gradient-to-b from-sea-400 to-sea-600 text-white border-sky-300',
};

export function Button({
  variant = 'primary',
  loading,
  className,
  children,
  onClick,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }) {
  return (
    <button
      type="button"
      className={cx(
        'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border-2 px-4 font-display text-[16px] font-bold transition active:translate-y-[1px] disabled:opacity-50',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-300',
        variants[variant],
        className,
      )}
      disabled={loading || rest.disabled}
      aria-busy={loading || undefined}
      onClick={(e) => {
        play('click');
        haptic(8);
        onClick?.(e);
      }}
      {...rest}
    >
      {loading ? <Spinner size={18} /> : children}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  onClick,
  className,
  badge,
  iconSize = 24,
  tut,
}: {
  icon: UiIcon;
  label: string;
  onClick: () => void;
  className?: string;
  badge?: number;
  tut?: string;
  iconSize?: number;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-tut={tut}
      onClick={() => {
        play('click');
        haptic(8);
        onClick();
      }}
      className={cx(
        'relative inline-flex h-11 w-11 items-center justify-center rounded-xl text-parchment-200 hover:text-gold-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-300',
        className,
      )}
    >
      <Icon name={icon} size={iconSize} />
      {badge ? <Badge className="absolute -right-1 -top-1">{badge > 99 ? '99+' : badge}</Badge> : null}
    </button>
  );
}

const fieldCls =
  'w-full min-h-[44px] rounded-xl border-2 border-ink-600 bg-ink-800 px-3 text-[16px] text-parchment-100 placeholder:text-parchment-300/60 focus:border-gold-400 focus:outline-none';

export function Input({
  label,
  error,
  hint,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string; hint?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className={cx('block space-y-1', className)}>
      {label && <span className="text-sm text-parchment-200">{label}</span>}
      <input
        id={id}
        className={cx(fieldCls, error && 'border-blood-400')}
        aria-invalid={!!error || undefined}
        aria-describedby={error || hint ? `${id}-d` : undefined}
        {...rest}
      />
      {(error || hint) && (
        <span
          id={`${id}-d`}
          className={cx('block text-sm', error ? 'text-blood-400' : 'text-parchment-300/80')}
        >
          {error ?? hint}
        </span>
      )}
    </label>
  );
}

export function Textarea({
  label,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string }) {
  const id = useId();
  return (
    <label htmlFor={id} className={cx('block space-y-1', className)}>
      {label && <span className="text-sm text-parchment-200">{label}</span>}
      <textarea id={id} className={cx(fieldCls, 'min-h-[96px] py-2')} {...rest} />
    </label>
  );
}

export function Select({
  label,
  options,
  className,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  options: { value: string; label: string; disabled?: boolean }[];
}) {
  const id = useId();
  return (
    <label htmlFor={id} className={cx('block space-y-1', className)}>
      {label && <span className="text-sm text-parchment-200">{label}</span>}
      <select id={id} className={fieldCls} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
}: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-[16px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-6 w-6 shrink-0 accent-gold-400"
      />
      <span>{label}</span>
    </label>
  );
}

export function Radio<T extends string>({
  name,
  value,
  options,
  onChange,
}: { name: string; value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" className="grid gap-2">
      {options.map((o) => (
        <label
          key={o.value}
          className={cx(
            'flex min-h-[48px] cursor-pointer items-center gap-3 rounded-xl border-2 px-3',
            value === o.value ? 'border-gold-400 bg-ink-700' : 'border-ink-600 bg-ink-800',
          )}
        >
          <input
            type="radio"
            name={name}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="h-5 w-5 accent-gold-400"
          />
          <span className="flex-1">{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export function Range({
  label,
  value,
  onChange,
}: { label: string; value: number; onChange: (v: number) => void }) {
  const id = useId();
  return (
    <label htmlFor={id} className="block">
      <span className="flex justify-between text-sm">
        <span>{label}</span>
        <span>{value}</span>
      </span>
      <Slider id={id} min={0} max={100} step={5} value={value} onChange={onChange} />
    </label>
  );
}

// Бегунок количества с маленькими кнопками «−» / «+» по бокам. Удержание кнопки — автоповтор с ускорением.
export function Slider({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  disabled,
  id,
  className,
  inputClassName = 'h-11',
  'aria-label': ariaLabel,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max: number;
  step?: number;
  disabled?: boolean;
  id?: string;
  className?: string;
  inputClassName?: string;
  'aria-label'?: string;
}) {
  const v = Math.max(min, Math.min(max, value));
  const cur = useRef(v);
  cur.current = v;
  const cb = useRef(onChange);
  cb.current = onChange;
  const timer = useRef<number | undefined>(undefined);
  const stop = () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  };
  useEffect(() => stop, []);
  const nudge = (dir: 1 | -1, mult = 1): boolean => {
    const next = Math.max(min, Math.min(max, cur.current + dir * step * mult));
    if (next === cur.current) return false;
    cur.current = next;
    cb.current(next);
    return true;
  };
  const hold = (dir: 1 | -1) => {
    stop();
    if (!nudge(dir)) return;
    const big = Math.max(1, Math.ceil((max - min) / step / 40));
    let n = 0;
    const tick = () => {
      n++;
      if (!nudge(dir, n < 12 ? 1 : n < 30 ? Math.max(1, Math.ceil(big / 4)) : big)) return stop();
      timer.current = window.setTimeout(tick, n < 12 ? 110 : 60);
    };
    timer.current = window.setTimeout(tick, 400);
    window.addEventListener('pointerup', stop, { once: true });
    window.addEventListener('pointercancel', stop, { once: true });
  };
  const btn = (dir: 1 | -1) => {
    const off = disabled || (dir < 0 ? v <= min : v >= max);
    return (
      <button
        type="button"
        disabled={off}
        aria-label={`${ariaLabel ? `${ariaLabel} ` : ''}${dir < 0 ? '−' : '+'}${step}`}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          hold(dir);
        }}
        onPointerLeave={stop}
        onClick={(e) => {
          // клавиатура (Enter/Space): у мыши/тача шаг уже сделан в pointerdown
          if (e.detail === 0) nudge(dir);
        }}
        onContextMenu={(e) => e.preventDefault()}
        className={cx(
          'grid h-8 w-8 shrink-0 touch-manipulation select-none place-items-center rounded-full border text-lg font-bold leading-none',
          'border-gold-400/70 bg-ink-800 text-gold-300 active:scale-95 active:bg-ink-700',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-300',
          'disabled:border-ink-600 disabled:text-parchment-300/30 disabled:active:scale-100',
        )}
      >
        {dir < 0 ? '−' : '+'}
      </button>
    );
  };
  return (
    <div className={cx('flex min-w-0 items-center gap-1.5', className)}>
      {btn(-1)}
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={v}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className={cx('min-w-0 flex-1 accent-gold-400', inputClassName)}
        aria-label={ariaLabel}
      />
      {btn(1)}
    </div>
  );
}

/** Поле для ввода количества цифрами: только целые 0…max, пустое поле при вводе — 0.
 *  Шрифт 16px, чтобы iOS не увеличивал страницу при фокусе. */
export function NumberField({
  value,
  onChange,
  max,
  disabled,
  className,
  'aria-label': ariaLabel,
}: {
  value: number;
  onChange: (v: number) => void;
  max: number;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  // Пока поле в фокусе, показываем то, что набрано (в т.ч. пустую строку)
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="off"
      enterKeyHint="done"
      disabled={disabled}
      value={draft ?? String(value)}
      onFocus={(e) => {
        setDraft(String(value));
        e.currentTarget.select();
      }}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
        const n = Math.min(max, Number(digits || 0));
        setDraft(digits === '' ? '' : String(n));
        onChange(n);
      }}
      onBlur={() => setDraft(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      className={cx(
        'h-9 min-w-0 rounded-lg border border-gold-500/50 bg-ink-800 px-1 text-center text-base tabular-nums text-parchment-50',
        'focus:border-gold-300 focus:outline-none focus:ring-1 focus:ring-gold-300 disabled:opacity-40',
        className,
      )}
      aria-label={ariaLabel}
    />
  );
}

/** Строка выбора количества (войска, корабли) в одну линию:
 *  картинка · название и «/всего» (нажатие — взять всех) · поле для цифр · «− ползунок +». */
export function CountRow({
  pic,
  label,
  value,
  max,
  total = max,
  onChange,
}: {
  pic?: ReactNode;
  label: string;
  value: number;
  /** Сколько можно выбрать сейчас (может быть меньше total, например из-за вместимости). */
  max: number;
  /** Сколько всего есть — показывается под названием. */
  total?: number;
  onChange: (v: number) => void;
}) {
  const v = Math.max(0, Math.min(max, value));
  return (
    <div className="flex items-center gap-1">
      {pic && <span className="shrink-0">{pic}</span>}
      <span className="w-[4.75rem] shrink-0 text-xs leading-tight">
        <span className="line-clamp-2 hyphens-auto break-words">{label}</span>
        <button
          type="button"
          disabled={max <= 0 || v >= max}
          onClick={() => onChange(max)}
          className="tabular-nums text-parchment-300 disabled:cursor-default"
          aria-label={`${label}: ${fmt(max)}`}
        >
          /{fmt(total)}
        </button>
      </span>
      <NumberField
        value={v}
        max={max}
        disabled={max <= 0}
        onChange={onChange}
        className="w-[4.75rem] shrink-0"
        aria-label={label}
      />
      <Slider
        min={0}
        max={max}
        value={v}
        disabled={max <= 0}
        onChange={onChange}
        className="flex-1"
        inputClassName="h-9"
        aria-label={label}
      />
    </div>
  );
}

// ───────── Контейнеры ─────────
export function Card({
  children,
  className,
  onClick,
  style,
  tut,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  style?: CSSProperties;
  /** Метка для обучения: сюда может указать подсказка «нажми здесь». */
  tut?: string;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      style={style}
      data-tut={tut}
      className={cx(
        'block w-full rounded-2xl border border-ink-600 bg-ink-800/90 p-3 text-left shadow-lg',
        onClick &&
          'active:scale-[.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-300',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function Panel({
  title,
  children,
  actions,
  className,
}: { title?: ReactNode; children: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <section
      className={cx(
        'rounded-2xl border-2 border-gold-600/60 bg-gradient-to-b from-ink-800 to-ink-900 p-3',
        className,
      )}
    >
      {(title || actions) && (
        <header className="mb-2 flex items-center justify-between gap-2">
          {title && <h2 className="font-display text-lg font-bold text-gold-300">{title}</h2>}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  className,
  fill = false,
}: {
  /** alert — во вкладке новое: неактивная кнопка мигает (как значок почты). */
  tabs: { value: T; label: ReactNode; badge?: number; alert?: boolean }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
  /** Вкладки делят ширину поровну (без прокрутки) — чтобы на телефоне были видны все. */
  fill?: boolean;
}) {
  return (
    <div
      role="tablist"
      className={cx('no-scrollbar flex gap-1 overflow-x-auto rounded-xl bg-ink-800 p-1', className)}
    >
      {tabs.map((t) => (
        <button
          key={t.value}
          role="tab"
          type="button"
          aria-selected={value === t.value}
          onClick={() => {
            haptic(6);
            onChange(t.value);
          }}
          className={cx(
            'relative min-h-[40px] rounded-lg font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-300',
            fill ? 'min-w-0 flex-auto whitespace-nowrap px-1 text-[13px]' : 'shrink-0 px-3 text-[15px]',
            value === t.value ? 'bg-gold-500 text-night' : 'text-parchment-200',
            t.alert && value !== t.value && 'mail-unread-blink',
          )}
        >
          {t.label}
          {t.badge ? <Badge className="ml-1">{t.badge}</Badge> : null}
        </button>
      ))}
    </div>
  );
}

export function Accordion({
  title,
  children,
  defaultOpen = false,
  onOpen,
  className,
}: {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  onOpen?: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className={cx('rounded-xl border border-ink-600', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          if (!open) onOpen?.();
          setOpen((o) => !o);
        }}
        className="flex min-h-[44px] w-full items-center justify-between px-3 font-semibold"
      >
        {title}
        <span aria-hidden className={cx('transition', open && 'rotate-180')}>
          ▾
        </span>
      </button>
      {open && (
        <div id={id} className="animate-fade-in px-3 pb-3">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Боковая колонка кнопок поверх карты/замка: по умолчанию свёрнута в одну кнопку «⋮», чтобы не
 * закрывать обзор. Выбор игрока (развёрнута или нет) запоминается.
 */
export function SideRail({
  id,
  side = 'right',
  children,
  className,
  alert = false,
}: {
  id: string;
  side?: 'left' | 'right';
  children: ReactNode;
  className?: string;
  /** Что-то требует внимания (атака, награда, отчёт) — кнопка меню мигает красным. */
  alert?: boolean;
}) {
  const { t } = useTranslation();
  const storageKey = `sk:rail:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () => {
    haptic(6);
    setOpen((o) => {
      try {
        localStorage.setItem(storageKey, o ? '0' : '1');
      } catch {
        // приватный режим — просто не запоминаем
      }
      return !o;
    });
  };
  return (
    <div
      className={cx(
        'pointer-events-auto flex flex-col gap-1',
        side === 'right' ? 'items-end' : 'items-start',
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-label={t(open ? 'hidePanel' : 'showPanel')}
        title={t(open ? 'hidePanel' : 'showPanel')}
        onClick={toggle}
        className={cx(
          'flex h-11 w-11 items-center justify-center rounded-xl border bg-ink-950/80 text-xl font-bold text-gold-200 shadow',
          // Развёрнутое меню не мигает: мигают сами кнопки с новостями
          alert && !open ? 'animate-alert-blink border-blood-400' : 'border-gold-600/70',
        )}
      >
        {open ? '▴' : '⋮'}
      </button>
      {open && <div className="flex animate-fade-in flex-col gap-1">{children}</div>}
    </div>
  );
}

export function ProgressBar({
  value,
  max = 1,
  label,
  className,
  color = 'gold',
}: {
  value: number;
  max?: number;
  label?: string;
  className?: string;
  color?: 'gold' | 'sea' | 'blood' | 'moss';
}) {
  const pct = Math.max(0, Math.min(100, (value / Math.max(1e-9, max)) * 100));
  const c = {
    gold: 'from-gold-300 to-gold-500',
    sea: 'from-sea-400 to-sea-500',
    blood: 'from-blood-400 to-blood-500',
    moss: 'from-moss-400 to-moss-500',
  }[color];
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cx('h-3 w-full overflow-hidden rounded-full bg-ink-950/80 ring-1 ring-ink-600', className)}
    >
      <div
        className={cx('h-full rounded-full bg-gradient-to-r transition-[width] duration-300', c)}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function Badge({
  children,
  className,
  tone = 'red',
}: { children: ReactNode; className?: string; tone?: 'red' | 'gold' | 'sea' | 'gray' }) {
  const t = {
    red: 'bg-blood-500 text-white',
    gold: 'bg-gold-400 text-night',
    sea: 'bg-sea-500 text-white',
    gray: 'bg-ink-600 text-parchment-100',
  }[tone];
  return (
    <span
      className={cx(
        'inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-bold leading-5',
        t,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Avatar({
  svg,
  src,
  alt,
  size = 48,
  frame = 'gold',
}: { svg?: string; src?: string | null; alt: string; size?: number; frame?: 'gold' | 'silver' }) {
  return (
    <span
      className={cx(
        'inline-flex overflow-hidden rounded-full ring-2',
        frame === 'gold' ? 'ring-gold-400' : 'ring-parchment-300',
      )}
    >
      {src ? (
        <img src={src} alt={alt} width={size} height={size} className="object-cover" />
      ) : svg ? (
        <Svg svg={svg} alt={alt} size={size} />
      ) : null}
    </span>
  );
}

export function Spinner({ size = 24 }: { size?: number }) {
  return (
    <span
      role="status"
      aria-label="loading"
      className="inline-block animate-spin rounded-full border-2 border-gold-300 border-t-transparent"
      style={{ width: size, height: size }}
    />
  );
}

export function Empty({ text }: { text?: string }) {
  const { t } = useTranslation();
  return <p className="py-6 text-center text-parchment-300/80">{text ?? t('empty')}</p>;
}

export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-3 py-10 text-parchment-200">
      <Spinner /> {t('loading')}
    </div>
  );
}

// ───────── Модальные окна: BottomSheet (mobile), Modal, Drawer ─────────
function useFocusTrap(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  // onClose в ref: эффект зависит только от open (иначе фокус прыгает при каждом рендере таймеров)
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const focusables = () =>
      Array.from(
        el?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((x) => !x.hasAttribute('disabled'));
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'Tab') {
        const f = focusables();
        if (f.length === 0) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus();
    };
  }, [open]);
  return ref;
}

/** Заголовок окна с крестиком: закрывает окно и возвращает туда, откуда его открыли. */
export function CloseButton({ onClose, className }: { onClose: () => void; className?: string }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={t('close')}
      title={t('close')}
      data-tut="window-close"
      onClick={() => {
        haptic(6);
        onClose();
      }}
      className={cx(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold-600/70 bg-ink-800 text-xl font-bold leading-none text-parchment-100 hover:bg-ink-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-400',
        className,
      )}
    >
      ✕
    </button>
  );
}

function WindowHeader({ title, onClose }: { title?: ReactNode; onClose: () => void }) {
  return (
    <div className="mb-3 flex items-start justify-between gap-2">
      {title ? <h2 className="pt-1.5 font-display text-xl font-bold text-gold-300">{title}</h2> : <span />}
      <CloseButton onClose={onClose} />
    </div>
  );
}

/**
 * Затемнение под окном; нажатие закрывает окно. Окна часто открываются по pointerup (касание сцены
 * замка, полей, карты), а «клик» того же касания приходит позже и попал бы уже в затемнение —
 * окно закрылось бы сразу. Поэтому первые 400 мс после появления нажатия игнорируются.
 */
function Backdrop({ className, onClose }: { className: string; onClose: () => void }) {
  const shownAt = useRef(performance.now());
  return (
    <button
      type="button"
      aria-label="close"
      className={className}
      onClick={() => {
        if (performance.now() - shownAt.current > 400) onClose();
      }}
    />
  );
}

export function BottomSheet({
  open,
  onClose,
  title,
  children,
}: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode }) {
  const ref = useFocusTrap(open, onClose);
  const startY = useRef<number | null>(null);
  const [drag, setDrag] = useState(0);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center" role="presentation">
      <Backdrop className="absolute inset-0 animate-fade-in bg-black/60" onClose={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        className="relative max-h-[88vh] w-full animate-slide-up overflow-y-auto rounded-t-3xl border-t-2 border-gold-500 bg-ink-900 px-4 pb-[calc(16px+env(safe-area-inset-bottom))] pt-2 md:max-w-lg md:rounded-3xl md:border-2"
        style={{ transform: drag > 0 ? `translateY(${drag}px)` : undefined }}
        onTouchStart={(e) => {
          if ((ref.current?.scrollTop ?? 0) <= 0) startY.current = e.touches[0]?.clientY ?? null;
        }}
        onTouchMove={(e) => {
          if (startY.current !== null) setDrag(Math.max(0, (e.touches[0]?.clientY ?? 0) - startY.current));
        }}
        onTouchEnd={() => {
          if (drag > 110) onClose();
          setDrag(0);
          startY.current = null;
        }}
      >
        <div aria-hidden className="mx-auto mb-2 h-1.5 w-12 rounded-full bg-ink-600" />
        <WindowHeader title={title} onClose={onClose} />
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
}: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode }) {
  const ref = useFocusTrap(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <Backdrop className="absolute inset-0 bg-black/60" onClose={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md animate-pop rounded-2xl border-2 border-gold-500 bg-ink-900 p-4"
      >
        <WindowHeader title={title} onClose={onClose} />
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({
  open,
  onClose,
  children,
  title,
}: { open: boolean; onClose: () => void; children: ReactNode; title?: ReactNode }) {
  const ref = useFocusTrap(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50">
      <Backdrop className="absolute inset-0 bg-black/60" onClose={onClose} />
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        className="absolute right-0 top-0 h-full w-[84%] max-w-sm animate-fade-in overflow-y-auto border-l-2 border-gold-500 bg-ink-900 p-4 pt-[calc(16px+env(safe-area-inset-top))]"
      >
        <WindowHeader title={title} onClose={onClose} />
        {children}
      </aside>
    </div>,
    document.body,
  );
}

export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      {children}
      {show && (
        <span
          role="tooltip"
          className="absolute bottom-full left-1/2 z-40 mb-1 w-max max-w-[220px] -translate-x-1/2 rounded-lg bg-ink-950 px-2 py-1 text-sm shadow"
        >
          {text}
        </span>
      )}
    </span>
  );
}

export function Popover({ trigger, children }: { trigger: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="min-h-[44px]">
        {trigger}
      </button>
      {open && (
        <span className="absolute right-0 top-full z-40 mt-1 min-w-[180px] animate-pop rounded-xl border border-gold-600 bg-ink-900 p-2 shadow-xl">
          {children}
        </span>
      )}
    </span>
  );
}

// ───────── Уведомления: Toast + aria-live ─────────
export function Toaster() {
  const toasts = useGame((s) => s.toasts);
  const dismiss = useGame((s) => s.dismiss);
  const colors = {
    info: 'border-sea-400',
    success: 'border-moss-400',
    error: 'border-blood-400',
    warning: 'border-gold-400',
  };
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 top-[calc(56px+env(safe-area-inset-top))] z-[60] flex flex-col items-center gap-2 px-3"
    >
      {toasts.map((t) => (
        <button
          type="button"
          key={t.id}
          onClick={() => dismiss(t.id)}
          className={cx(
            'pointer-events-auto w-full max-w-md animate-pop rounded-xl border-l-4 bg-ink-800/95 px-4 py-3 text-left text-[15px] text-parchment-100 shadow-xl backdrop-blur',
            colors[t.kind],
          )}
        >
          {t.text}
        </button>
      ))}
    </div>
  );
}

// ───────── Таблица и пагинация ─────────
export function Table<T>({
  rows,
  columns,
  rowKey,
  empty,
}: {
  rows: T[];
  columns: { key: string; title: ReactNode; render: (r: T) => ReactNode; className?: string }[];
  rowKey: (r: T) => string;
  empty?: string;
}) {
  if (rows.length === 0) return <Empty text={empty} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[15px]">
        <thead>
          <tr className="text-left text-parchment-300">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cx('border-b border-ink-600 px-2 py-2 font-semibold', c.className)}
              >
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} className="border-b border-ink-700 odd:bg-ink-800/40">
              {columns.map((c) => (
                <td key={c.key} className={cx('px-2 py-2 align-top', c.className)}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({
  page,
  total,
  perPage,
  onChange,
}: { page: number; total: number; perPage: number; onChange: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  return (
    <nav aria-label="pagination" className="flex items-center justify-center gap-2 py-2">
      <Button variant="secondary" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        ‹
      </Button>
      <span className="text-sm">
        {page} / {pages}
      </span>
      <Button variant="secondary" disabled={page >= pages} onClick={() => onChange(page + 1)}>
        ›
      </Button>
    </nav>
  );
}

// ───────── Таймеры и стоимость ─────────
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(serverNow());
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') setNow(serverNow());
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function Countdown({
  to,
  onDone,
  className,
}: { to: string | number; onDone?: () => void; className?: string }) {
  const now = useNow();
  const target = typeof to === 'number' ? to : new Date(to).getTime();
  const left = Math.max(0, (target - now) / 1000);
  const fired = useRef(false);
  useEffect(() => {
    if (left <= 0 && !fired.current) {
      fired.current = true;
      onDone?.();
    }
  }, [left, onDone]);
  return (
    <span className={cx('tabular-nums', className)} aria-live="off">
      {left >= 86_400 ? span(left) : duration(left)}
    </span>
  );
}

export function Cost({
  cost,
  have,
}: { cost: Partial<ResourceMap> & { gems?: number }; have?: Partial<ResourceMap> | null }) {
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {Object.entries(cost).map(([k, v]) => {
        const lack = have && k in have && (have[k as keyof ResourceMap] ?? 0) < (v ?? 0);
        return (
          <span
            key={k}
            className={cx('inline-flex items-center gap-1 tabular-nums', lack && 'text-blood-400')}
          >
            <ResIcon type={k as keyof ResourceMap} size={18} />
            {short(v ?? 0)}
          </span>
        );
      })}
    </span>
  );
}
